import _t from 'i18n!nls/program-skills-objectives';

/**
 * These are common tab configs used on more than one tab.
 */
function getSharedInformationTabs() {
  const howSkillSetsWork = {
    id: 'tab~IzeOg5Je9KRBQ5R80KuO4',
    label: _t('How SkillSets work'),
    content: [
      {
        id: 'column~dQhJT7BDe9uKL47kCu7wp',
        value: [
          {
            id: 'cell~YVIcp77y4SBBVKkEUy8Yw',
            value: _t(
              'Admins in your organization can define SkillSets based on business goals and link those individual SkillSets to Learning Programs. This helps learners within your organization guide their learning journey.'
            ),
          },
          {
            id: 'cell~w-Sxfus8sfJeyeGMwazRX',
            value: _t(
              'Learners can find SkillSets that are compatible with their current role and future career goals.'
            ),
          },
        ],
      },
      {
        id: 'column~K8HZ-VZp3CLr8Pr6ol2Li',
        value: [
          {
            id: 'cell~P7o3Glx4eYsG5GiMUcJsn',
            value: _t(
              'They can view their own skill proficiency scores and work towards the target proficiency score shown in the SkillSet.'
            ),
          },
          {
            id: 'cell~y57zwT7GbCad8COIjstUi',
            value: _t(
              'Course recommendations show learners which skills are covered in different courses. This way they can develop a learning plan specific to skills they want to develop.'
            ),
          },
        ],
      },
    ],
  };

  const howSkillScoresAreCalculated = {
    id: 'tab~-R0FAg7EiHP-AagzfPjxC',
    label: _t('How skill scores are calculated'),
    content: [
      {
        id: 'column~vaSHPdNzZBtPImQHvgmrR',
        value: [
          {
            id: 'cell~-2dHqiPZGL2rCKHqhq51z',
            value: _t(
              'Skill proficiency scores are calculated based on learner performance on graded assessments. As learners pass assessments, their skill score generally increases, but how much depends on the difficulty of the material.'
            ),
          },
          {
            id: 'cell~kl7IzRw_VvirSnHWuKNFn',
            value: _t(
              'Their performance on assessments is weighted by difficulty, similar to a rating in competitive sports. If they pass harder assessments, their score will increase faster.'
            ),
          },
        ],
      },
      {
        id: 'column~Kept-y47Z0IXhoVKGw0G0',
        value: [
          {
            id: 'cell~Ze_LPISXeRKnqsNTJ1D8C',
            value: _t(
              'If they pass easier assessments, their score will increase slower. The higher they go, the more advanced content they will need to take to increase their score.'
            ),
          },
          {
            id: 'cell~yvJNr-U7bBXXMBgqrLw2e',
            value: _t(
              'If you want to know more about the data model that powers skill scoring, read <a target="_blank" rel="nofollow noopener" href="https://files.eric.ed.gov/fulltext/ED599237.pdf">this research paper</a> that explains it in detail. '
            ),
          },
        ],
      },
    ],
  };

  const howLearnersDevelopSkills = {
    id: 'tab~7B6BAs4r3tBTYh8MQdfR2',
    label: _t('How learners develop skills'),
    content: [
      {
        id: 'column~kqTTekAhE7AG_V23dU4rG',
        value: [
          {
            id: 'cell~oN1ZAiZiq7JOToGkjmt2u',
            value: _t(
              'Upon joining Coursera, a learner’s initial proficiency score for a given skill is zero. That proficiency score increases when the learner passes graded assessments tagged with that skill.'
            ),
          },
          {
            id: 'cell~H_G47FGlxEFCTX1Gcvckg',
            value: _t(
              'Learners enroll in one or more recommended courses and when they pass harder assessments, their skill score will increase faster.'
            ),
          },
        ],
      },
      {
        id: 'column~wfS_a1JNiGa_mAnz54Yb6',
        value: [
          {
            id: 'cell~TMxjDeuuMk_zFxJSVk1hY',
            value: _t(
              'The higher their score is, the more advanced content they will need to take to increase their score for that skill.'
            ),
          },
        ],
      },
    ],
  };

  return {
    howSkillSetsWork,
    howSkillScoresAreCalculated,
    howLearnersDevelopSkills,
  };
}

export function getWhatAreSkillSetsConfig() {
  const { howSkillSetsWork, howLearnersDevelopSkills, howSkillScoresAreCalculated } = getSharedInformationTabs();

  const whatAreSkillSets = {
    id: 'tab~ZVNcLBmSMcUm7M4domzWA',
    label: _t('What are SkillSets'),
    content: [
      {
        id: 'column~x4oSmq00vPy61ZdilLKpV',
        value: [
          {
            id: 'cell~EplU9dt_1Ljys0aiFmzjF',
            value: _t(
              'SkillSets are learning paths defined by your organization to help learners develop career-relevant skills.'
            ),
          },
          {
            id: 'cell~E_WGcvlIpS9ZUVHqa_7mZ',
            value: _t(
              'Each SkillSet will match learners with the right skills and content to become competitive professionals prepared for real-world, on-the-job responsibilities.'
            ),
          },
        ],
      },
      {
        id: 'column~vzJdsOJTGsTpeNx1XJVg2',
        value: [
          {
            id: 'cell~Q3D9XBHsXzZXy5Gzg91h5',
            value: _t(
              'They include proficiency targets that provide learners with skill proficiency expectations that will guide their learning journey.'
            ),
          },
          {
            id: 'cell~iEs4gFe5AhqhqfRKjKfuT',
            value: _t(
              'They also include skill-based course recommendations that help learners master the skills they need to perform in their current job or in a future position.'
            ),
          },
        ],
      },
    ],
  };

  return [whatAreSkillSets, howSkillSetsWork, howLearnersDevelopSkills, howSkillScoresAreCalculated];
}

export function getHowLearnersDevelopSkillsConfig() {
  const { howSkillScoresAreCalculated, howLearnersDevelopSkills } = getSharedInformationTabs();
  return [howLearnersDevelopSkills, howSkillScoresAreCalculated];
}

export function getHowSkillSetsWorkConfig() {
  const { howSkillSetsWork } = getSharedInformationTabs();

  const whatAreProficiencyTargets = {
    id: 'tab~E6rZkHzhlSmDojAx5aUd1',
    label: _t('What are proficiency targets'),
    content: [
      {
        id: 'column~P_0FPrhezgCESspt0X7Yk',
        value: [
          {
            id: 'cell~svfSp0lnqlwr2VlkwfHZv',
            value: _t(
              'Proficiency targets are numerical scores defined by Admins in your organization to communicate skill development goals and expectations to learners.'
            ),
          },
          {
            id: 'cell~LPS6dMJDLgPDoPsKrHaWj',
            value: _t(
              'Each skill in a SkillSet has a proficiency target that learners must achieve. Once they achieve the proficiency target for all skills in the SkillSet they are considered as having achieved that SkillSet.'
            ),
          },
        ],
      },
      {
        id: 'column~W0JkP--WTl2Y8DOfXy-RE',
        value: [
          {
            id: 'cell~Ckp7QKx2zkR3A1v3iytOq',
            value: _t(
              'Proficiency targets are originally set using Coursera platform data and subject matter experts, but can be customized by Admins in your organization.'
            ),
          },
        ],
      },
    ],
  };

  const howToAddAndLinkSkillSets = {
    id: 'tab~llAYrscCxFFXUOm0SIZU-',
    label: _t('How to add and link SkillSets'),
    content: [
      {
        id: 'column~JBvx_dpBCO921dUPrE7za',
        value: [
          {
            id: 'cell~R43webwXb9yHBeRPoPoak',
            value: _t(
              "<p>Administrators have the ability to add SkillSets into the organization, by using the '+ Add SkillSets' function from the main SkillSets area.</p><p>Once SkillSets are in your organization, administrators can choose to add them to learning programs, either from the main SkillSet area or from within a program; in turn making them available to learners.</p>"
            ),
          },
        ],
      },
    ],
  };

  return [howSkillSetsWork, whatAreProficiencyTargets, howToAddAndLinkSkillSets];
}
